import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbMarkdownExtensions } from "abreact/input/markdown/AbMarkdownExtensions";
import { AbLinkComponent } from "abreact/input/markdown/nodes/AbLinkComponent";
import { URLUtils } from "abweb/utils/URLUtils";
import { DecoratorNode } from 'lexical';
import { $applyNodeReplacement } from 'lexical';
import React from "react";
import { $isLinkNode } from "@lexical/link";
function convertAbLinkElement(domNode) {
  const text = domNode.innerText;
  const url = domNode.getAttribute('data-lexical-url') || "";
  const node = $createAbLinkNode(text, url);
  return {
    node
  };
}
export class AbLinkNode extends DecoratorNode {
  static getType() {
    return 'abLink';
  }
  static clone(node) {
    return new AbLinkNode(node.text, node.url, node.__key);
  }
  constructor(text, url, key) {
    super(key);
    _defineProperty(this, "text", void 0);
    _defineProperty(this, "url", void 0);
    this.text = text;
    this.url = url;
  }
  static importJSON(serializedNode) {
    return $createAbLinkNode(serializedNode.text, serializedNode.url);
  }
  exportJSON() {
    return {
      text: this.text,
      url: this.url,
      type: 'abLink',
      version: 1
    };
  }
  updateDOM(prevNode) {
    return prevNode.text !== prevNode.text || prevNode.url !== prevNode.url;
  }
  createDOM() {
    const dom = document.createElement('span');
    dom.style.display = 'inline-block';
    return dom;
  }
  exportDOM() {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-ab-link', 'true');
    element.setAttribute("data-lexical-url", this.url);
    element.append(this.text);
    return {
      element
    };
  }
  static importDOM() {
    return {
      span: domNode => {
        if (!domNode.hasAttribute('data-lexical-ab-link')) {
          return null;
        }
        return {
          conversion: convertAbLinkElement,
          priority: 1
        };
      }
    };
  }
  decorate() {
    let contents = /*#__PURE__*/React.createElement("span", {
      className: "ab-markdown-abLink-default"
    }, this.text);
    const protocol = URLUtils.getProtocol(this.url);
    const extension = protocol ? AbMarkdownExtensions.markdownLinkExtensions.get(protocol) : undefined;
    if (extension) {
      // make the child be the extension rendering
      const element = extension.renderLexical ? extension.renderLexical({
        url: this.url
      }, this.text) : extension.render({
        url: this.url
      }, this.text);
      if (/*#__PURE__*/React.isValidElement(element)) {
        contents = element;
      } else {
        throw new Error("Link renderer did not return valid element");
      }
    }
    return /*#__PURE__*/React.createElement(AbLinkComponent, {
      nodeKey: this.getKey(),
      linkText: this.text,
      linkUrl: this.url
    }, contents);
  }
  getTextContent() {
    return this.text;
  }
  insertBefore(nodeToInsert, restoreSelection) {
    // If the containing node is a link node, insert before THAT.  If not, no override.
    const parent = this.getParentOrThrow();
    if ($isLinkNode(parent)) {
      return parent.insertBefore(nodeToInsert, restoreSelection);
    } else {
      return super.insertBefore(nodeToInsert, restoreSelection);
    }
  }
  getUrl() {
    return this.url;
  }
  isIsolated() {
    return false;
  }
}
export function $createAbLinkNode(text, url) {
  const abLinkNode = new AbLinkNode(text, url);
  return $applyNodeReplacement(abLinkNode);
}
export function $isAbLinkNode(node) {
  // eslint-disable-next-line abinitio/no-instanceof
  return node instanceof AbLinkNode;
}
//# sourceMappingURL=AbLinkNode.js.map
